import {
  type BookingClient,
  type Location,
  LocationKind,
  type LocationRef,
  MeetingMode,
  type SchedulingEventType,
  SchedulingIntegrationState,
  type UserControls,
  type UserSchedulingSettings,
} from '../../../../models/Scheduling'
import { type CreateBookingPayload } from '../../../../store/scheduling/dto'
import { clientDisplayName } from '../../shared/schedulingDisplay'

export const hasBookableCalendar = (settings: UserSchedulingSettings): boolean =>
  settings.integrations?.calendars?.some((calendar) => calendar.connected && calendar.state === SchedulingIntegrationState.LIVE && calendar.capable) ?? false

export const suggestMeetingTitle = (clients: BookingClient[], ownerName: string, eventType: SchedulingEventType | null): string => {
  if (!eventType) {
    return ''
  }
  const clientNames = clients.map((client) => clientDisplayName(client)).join(', ')
  const lead = clientNames ? `${clientNames} x ${ownerName}` : ownerName
  return `${lead} - ${eventType.name}`
}

export type InviteeSource = 'team-member' | 'contact' | 'email'

export const inviteeRoleLabel = (source: InviteeSource, contactType?: string): string => {
  if (source === 'team-member') {
    return 'Team member'
  }
  if (source === 'email') {
    return 'Guest'
  }
  return contactType || 'Contact'
}

export type DefaultLocationKind = 'event-type' | 'account' | 'user'

export interface ResolvedDefaultLocation {
  kind: DefaultLocationKind
  locationId: string
  location: Location
}

interface DefaultLocationSources {
  eventType: SchedulingEventType | null
  accountDefaultLocationId?: string
  userDefaultLocationId?: string
}

export const resolveDefaultLocations = (sources: DefaultLocationSources, locations: Location[]): ResolvedDefaultLocation[] => {
  const byKind: { kind: DefaultLocationKind; locationId?: string | null }[] = [
    { kind: 'event-type', locationId: sources.eventType?.location?.id },
    { kind: 'account', locationId: sources.accountDefaultLocationId },
    { kind: 'user', locationId: sources.userDefaultLocationId },
  ]
  return byKind.flatMap(({ kind, locationId }) => {
    if (!locationId) {
      return []
    }
    const location = locations.find((candidate) => candidate.id === locationId)
    return location ? [{ kind, locationId, location }] : []
  })
}

export const isSymbolicLocationKind = (kind?: LocationKind): boolean => kind === LocationKind.ACCOUNT_DEFAULT || kind === LocationKind.USER_DEFAULT

export const defaultOptionLocationRef = (kind: DefaultLocationKind, eventType: SchedulingEventType, resolvedLocationId: string): LocationRef => {
  if (kind === 'account') {
    return { kind: LocationKind.ACCOUNT_DEFAULT, id: resolvedLocationId }
  }
  if (kind === 'user') {
    return { kind: LocationKind.USER_DEFAULT, id: resolvedLocationId }
  }
  return { kind: eventType.location?.kind ?? LocationKind.FIXED, id: resolvedLocationId }
}

export const bookingLocationFields = (meetingMode: MeetingMode, location: LocationRef | null): Pick<CreateBookingPayload, 'location'> => {
  if (meetingMode !== MeetingMode.IN_PERSON || !location) {
    return {}
  }
  if (isSymbolicLocationKind(location.kind)) {
    return { location: { kind: location.kind } }
  }
  return location.id ? { location: { kind: LocationKind.FIXED, id: location.id } } : {}
}

export const resolveLocationPreviewId = (
  location: LocationRef | null | undefined,
  accountDefaultLocationId: string | null | undefined,
  userDefaultLocationId: string | null | undefined,
): string | null => {
  if (location?.kind === LocationKind.ACCOUNT_DEFAULT) {
    return accountDefaultLocationId ?? null
  }
  if (location?.kind === LocationKind.USER_DEFAULT) {
    return userDefaultLocationId ?? null
  }
  return location?.id ?? null
}

export const canManageOwnLocations = (userControls: UserControls | undefined): boolean => userControls?.allowManageOwnLocations !== false

export const countAffectedEventTypes = (
  eventTypes: Pick<SchedulingEventType, 'location'>[],
  current: Pick<Location, 'id'>,
  isChangeDefault: boolean,
  defaultKind?: LocationKind,
): number =>
  eventTypes.filter((eventType) => (isChangeDefault && defaultKind ? eventType.location?.kind === defaultKind : eventType.location?.id === current.id)).length
